/** @jsx jsx */

import { MarkingProvider } from '@markings/react';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { init as initialiseSentry } from '@sentry/react';
import { jsx } from '@balance-web/core';

import { LoadScript } from '@reckon-web/load-script';
import { RestApiClientProvider } from '@reckon-web/rest-api-client';
import { ErrorBoundaryProvider } from '@reckon-web/error-boundary';
import { LoadGoogleTagManagerService } from '@reckon-web/google-tag-manager';
import { LoadBranchIoService } from '@reckon-web/branchio';
import { ReckonBalanceCore } from '@reckon-web/reckon-balance-core';
import { urlWithPublicPrefix } from '@reckon-web/next-config/client';

import { BookIdProvider } from '../services/Book/BookIdProvider';
import { PermissionsProvider } from '../services/Permissions';
import { SiteRoutesProvider } from '../services/Navigation';
import { CompanyIdFromRouteProvider } from '../services/Company';
import { PortalAuthProvider } from '../services/Auth';
import { PortalApolloProvider } from '../services/PortalGqlApi';
import { PageBlockProvider } from '../components/generic/PageBlock/PageBlockProvider';
import { Config } from '../services/Config';

import { UniversalNextLink } from '@balance-web/next-link';

initialiseSentry({
  dsn: Config.APPLICATION_SENTRY_DSN,
  environment: Config.STAGE,
  release: Config.APPLICATION_VERSION,
});

(async () => {
  if ('undefined' === typeof window) {
    return;
  }

  LoadScript({
    parent: 'head',
    id: `newrelic-${Config.STAGE}`,
    src: urlWithPublicPrefix(`/newrelic-${Config.STAGE}.js`),
  });
  LoadGoogleTagManagerService({
    apiKey: Config.APPLICATION_GOOGLE_GTM_APIKEY,
  });
  LoadBranchIoService({
    apiKey: Config.APPLICATION_BRANCHIO_APIKEY,
  });
})();

export default function App({ Component, pageProps, router }: AppProps) {
  return (
    <MarkingProvider enabled={Config.STAGE !== 'prod'}>
      <ReckonBalanceCore themeMode="light" linkComponent={UniversalNextLink}>
        <ErrorBoundaryProvider onError={console.error}>
          <PortalAuthProvider>
            <PortalApolloProvider>
              <RestApiClientProvider>
                <CompanyIdFromRouteProvider>
                  <BookIdProvider>
                    <PermissionsProvider>
                      <SiteRoutesProvider>
                        <PageBlockProvider size="large">
                          <Head>
                            <title>
                              {Config.APPLICATION_LONG_DISPLAY_NAME}
                            </title>
                            <meta
                              name="viewport"
                              content="initial-scale=1.0, width=device-width"
                            />
                            <link
                              rel="apple-touch-icon"
                              sizes="180x180"
                              href={urlWithPublicPrefix(
                                '/public/apple-touch-icon.png'
                              )}
                            />
                            <link
                              rel="icon"
                              type="image/png"
                              sizes="32x32"
                              href={urlWithPublicPrefix(
                                '/public/favicon-32x32.png'
                              )}
                            />
                            <link
                              rel="icon"
                              type="image/png"
                              sizes="16x16"
                              href={urlWithPublicPrefix(
                                '/public/favicon-16x16.png'
                              )}
                            />

                            <link
                              rel="mask-icon"
                              href={urlWithPublicPrefix(
                                '/public/safari-pinned-tab.svg'
                              )}
                              color="#F84184"
                            />
                            <meta
                              name="msapplication-TileColor"
                              content="#FFFFFF"
                            />
                            <meta name="theme-color" content="#FFFFFF" />
                          </Head>
                          <Component {...pageProps} />
                        </PageBlockProvider>
                      </SiteRoutesProvider>
                    </PermissionsProvider>
                  </BookIdProvider>
                </CompanyIdFromRouteProvider>
              </RestApiClientProvider>
            </PortalApolloProvider>
          </PortalAuthProvider>
        </ErrorBoundaryProvider>
      </ReckonBalanceCore>
    </MarkingProvider>
  );
}
